/*
 * Pure helpers for the transfer drawer (PROJ-0078-WU-10). No pricing logic
 * lives here: amounts are the server's *_minor integers, only FORMATTED for
 * display (minor → major by the currency's own exponent). The app's existing
 * money idiom is `{amount} {CUR}` (Orders.tsx `money`), which takes major
 * units, so this is the minor-units counterpart in the same visual style.
 */

export type RecipientProblem = 'empty' | 'format' | 'self'

// Deliberately simple (the input is native type="email" too): one @, no
// spaces, a dot in the domain. The server is the real validator.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Client checks from the design note: format, and not the buyer's own
 *  email (case-insensitive). null ⇒ OK to submit. */
export function recipientProblem(raw: string, buyerEmail?: string | null): RecipientProblem | null {
  const email = raw.trim()
  if (!email) return 'empty'
  if (!EMAIL_RE.test(email)) return 'format'
  if (buyerEmail && email.toLowerCase() === buyerEmail.trim().toLowerCase()) return 'self'
  return null
}

function currencyDigits(currency: string): number {
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2
  } catch {
    // Unknown/blank code — 2 is the common exponent; the code still renders.
    return 2
  }
}

/** 12345, 'AED' → '123.45 AED'; -5000, 'AED' → '-50.00 AED'. Renders the
 *  server integer as-is (no rounding beyond the currency exponent). */
export function formatMinor(minor: number, currency: string): string {
  const digits = currencyDigits(currency)
  const major = minor / 10 ** digits
  const n = major.toLocaleString('en-GB', { minimumFractionDigits: digits, maximumFractionDigits: digits })
  return `${n} ${currency}`.trim()
}

/** Milliseconds left on the hold, or null when the timestamp is unreadable. */
export function holdRemainingMs(holdExpiresAt: string, now: number): number | null {
  const t = Date.parse(holdExpiresAt)
  if (Number.isNaN(t)) return null
  return Math.max(0, t - now)
}

/** 125_000 → '2:05'. */
export function formatCountdown(ms: number): string {
  const total = Math.ceil(ms / 1000)
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}
